// フォルダのピン止め状態管理
import { computed, ref } from 'vue';

export const useFolderPin = ({ folders, allowedFolderIds }) => {
  const pinnedFolderIds = ref([]);
  const pinToggleUrlTemplate = ref('');
  const pinToggleFailedMessage = ref('フォルダのピン止め更新に失敗しました。');
  const pinProcessingIds = ref(new Set());

  const normalizePinnedFolderIds = (rawIds) => {
    const source = Array.isArray(rawIds) ? rawIds : [];
    const folderLookup = new Set(
      (folders.value || [])
        .map((folder) => String(folder?.id ?? ''))
        .filter((id) => id !== '')
    );
    const allowedLookup =
      allowedFolderIds.value === null
        ? null
        : new Set(
            (allowedFolderIds.value || [])
              .map((id) => String(id ?? ''))
              .filter((id) => id !== '')
          );
    const seen = new Set();
    const normalizedIds = [];

    source.forEach((id) => {
      const parsed = Number(id);
      if (!Number.isFinite(parsed)) {
        return;
      }
      const intId = Math.trunc(parsed);
      if (intId <= 0) {
        return;
      }
      const key = String(intId);
      if (!folderLookup.has(key)) {
        return;
      }
      if (allowedLookup && !allowedLookup.has(key)) {
        return;
      }
      if (seen.has(key)) {
        return;
      }

      seen.add(key);
      normalizedIds.push(intId);
    });

    return normalizedIds;
  };

  const applyPinnedFolderIds = (folderIds) => {
    pinnedFolderIds.value = normalizePinnedFolderIds(folderIds);
  };

  const initFolderPins = (dataSource) => {
    if (!dataSource) {
      pinnedFolderIds.value = [];
      pinToggleUrlTemplate.value = '';
      return;
    }

    pinToggleUrlTemplate.value =
      dataSource.getAttribute('data-pin-toggle-url-template') || '';
    pinToggleFailedMessage.value =
      dataSource.getAttribute('data-pin-toggle-failed-message') ||
      pinToggleFailedMessage.value;

    const rawPinnedFolderIds = dataSource.getAttribute('data-pinned-folder-ids');
    if (!rawPinnedFolderIds) {
      pinnedFolderIds.value = [];
      return;
    }

    try {
      const parsedIds = JSON.parse(rawPinnedFolderIds);
      applyPinnedFolderIds(parsedIds);
    } catch (error) {
      pinnedFolderIds.value = [];
    }
  };

  const resolvePinToggleUrl = (folderId) => {
    const normalizedId = String(folderId ?? '');
    if (!normalizedId) {
      return null;
    }

    const template = pinToggleUrlTemplate.value;
    if (template && template.includes('__FOLDER_ID__')) {
      return template.replace('__FOLDER_ID__', normalizedId);
    }

    return `/dashboard/folders/${normalizedId}/pin`;
  };

  const isPinProcessing = (folderId) =>
    pinProcessingIds.value.has(String(folderId ?? ''));

  const setPinProcessing = (folderId, status) => {
    const normalizedId = String(folderId ?? '');
    if (!normalizedId) {
      return;
    }

    const next = new Set(pinProcessingIds.value);
    if (status) {
      next.add(normalizedId);
    } else {
      next.delete(normalizedId);
    }
    pinProcessingIds.value = next;
  };

  const toggleFolderPin = async (folderId) => {
    const normalizedId = String(folderId ?? '');
    if (!normalizedId || isPinProcessing(normalizedId)) {
      return;
    }

    const endpoint = resolvePinToggleUrl(normalizedId);
    if (!endpoint) {
      return;
    }

    setPinProcessing(normalizedId, true);
    try {
      const response = await window.axios.post(endpoint);
      const nextPinnedFolderIds = response?.data?.pinned_folder_ids;
      if (Array.isArray(nextPinnedFolderIds)) {
        applyPinnedFolderIds(nextPinnedFolderIds);
      } else {
        const isPinned = Boolean(response?.data?.is_pinned);
        const current = pinnedFolderIds.value.map((id) => String(id));
        if (isPinned) {
          if (!current.includes(normalizedId)) {
            pinnedFolderIds.value = [...pinnedFolderIds.value, Number(normalizedId)];
          }
        } else {
          pinnedFolderIds.value = pinnedFolderIds.value.filter(
            (id) => String(id) !== normalizedId
          );
        }
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || pinToggleFailedMessage.value;
      window.alert(message);
    } finally {
      setPinProcessing(normalizedId, false);
    }
  };

  const pinnedFolders = computed(() => {
    const folderMap = new Map(
      (folders.value || []).map((folder) => [String(folder.id), folder])
    );

    return pinnedFolderIds.value
      .map((folderId) => folderMap.get(String(folderId)))
      .filter(Boolean);
  });

  return {
    pinnedFolderIds,
    pinnedFolders,
    initFolderPins,
    applyPinnedFolderIds,
    toggleFolderPin,
    isPinProcessing,
  };
};
